/**
 * Mirrors packages/amghighlow_v2/lib/domain/entities/bet.dart `BetCollection`.
 */
import type { BetType } from '../types/game';
import { BET_ITEM_ID } from '../types/game';

export interface BetEntry {
  bet: BetType;
  amount: number;
  chips: number[];
}

export class BetCollection {
  private entries: Map<BetType, BetEntry> = new Map();

  static empty(): BetCollection {
    return new BetCollection();
  }

  copy(): BetCollection {
    const c = new BetCollection();
    for (const [k, v] of this.entries) {
      c.entries.set(k, { bet: v.bet, amount: v.amount, chips: [...v.chips] });
    }
    return c;
  }

  add(bet: BetType, amount: number): void {
    const cur = this.entries.get(bet);
    if (cur) {
      cur.amount += amount;
      cur.chips.push(amount);
    } else {
      this.entries.set(bet, { bet, amount, chips: [amount] });
    }
  }

  clear(): void {
    this.entries.clear();
  }

  clearBet(bet: BetType): void {
    this.entries.delete(bet);
  }

  amountFor(bet: BetType): number {
    return this.entries.get(bet)?.amount ?? 0;
  }

  chipsFor(bet: BetType): readonly number[] {
    return this.entries.get(bet)?.chips ?? [];
  }

  get total(): number {
    let n = 0;
    for (const v of this.entries.values()) n += v.amount;
    return n;
  }

  get isEmpty(): boolean {
    return this.entries.size === 0;
  }

  get bets(): readonly BetType[] {
    return Array.from(this.entries.keys());
  }

  /**
   * Convert into the body lines expected by /action/bet.
   * Mirrors `BetCollection.toApiFormat()` in
   *   packages/amghighlow_v2/lib/domain/entities/bet.dart:124-133
   */
  toBetLines(): { betItemId: number; amount: number }[] {
    const out: { betItemId: number; amount: number }[] = [];
    for (const v of this.entries.values()) {
      if (v.amount <= 0) continue;
      out.push({ betItemId: BET_ITEM_ID[v.bet], amount: v.amount });
    }
    return out;
  }
}
